import {
  type ComponentProps,
  type PropsWithChildren,
  Fragment,
  useCallback,
} from 'react';
import { Link } from 'react-router-dom';
import {
  CompoundEntityRef,
  DEFAULT_NAMESPACE,
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  getEntityRelations,
  getEntitySourceLocation,
  useEntity,
} from '@backstage/plugin-catalog-react';
import {
  TECHDOCS_ANNOTATION,
  TECHDOCS_EXTERNAL_ANNOTATION,
} from '@backstage/plugin-techdocs-common';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import { TdsIcon } from '@scania/tegel-react';
import { TegelCard } from '../common/Tegel';
import {
  entityDisplayName,
  humanizedRelationLabel,
  titleCaseEntityName,
  useResolvedEntityLabels,
} from './entityDisplay';
import { catalogEntityRoutePath, techDocsRoutePath } from '../Root/navigation';

export type GridEntityCardVariant = 'gridItem' | 'fullHeight';

type GridEntityAboutCardProps = {
  variant?: GridEntityCardVariant;
};

type EntitySpec = {
  lifecycle?: string;
  type?: string;
};

type TegelIconName = NonNullable<ComponentProps<typeof TdsIcon>['name']>;

const gitlabProjectSlugAnnotation = 'gitlab.com/project-slug';
const gitlabInstanceAnnotation = 'gitlab.com/instance';

const gridCardClassName = (variant: GridEntityCardVariant) =>
  variant === 'fullHeight'
    ? 'catalog-grid-card catalog-full-height-card catalog-entity-card-surface catalog-about-card-surface'
    : 'catalog-grid-card catalog-entity-card-surface catalog-about-card-surface';

const entitySpec = (entity: Entity) => entity.spec as EntitySpec | undefined;

const entityPath = (entityOrRef: Entity | CompoundEntityRef | string) => {
  if (typeof entityOrRef === 'string') {
    return catalogEntityRoutePath(
      parseEntityRef(entityOrRef, {
        defaultKind: 'component',
        defaultNamespace: DEFAULT_NAMESPACE,
      }),
    );
  }

  const ref =
    'metadata' in entityOrRef
      ? {
          kind: entityOrRef.kind,
          namespace: entityOrRef.metadata.namespace ?? DEFAULT_NAMESPACE,
          name: entityOrRef.metadata.name,
        }
      : {
          kind: entityOrRef.kind,
          namespace: entityOrRef.namespace ?? DEFAULT_NAMESPACE,
          name: entityOrRef.name,
        };

  return catalogEntityRoutePath(ref);
};

const MetaItem = ({
  children,
  label,
}: PropsWithChildren<{ label: string }>) => (
  <div className="tegel-entity-about-meta-item">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const RelationLinks = ({
  defaultKind,
  labelForRelation,
  relations,
}: {
  defaultKind: string;
  labelForRelation: (relation: CompoundEntityRef) => string;
  relations: CompoundEntityRef[];
}) => {
  if (!relations.length) {
    return <strong>No {defaultKind}</strong>;
  }

  return (
    <span className="tegel-entity-about-relation-list">
      {relations.map((relation, index) => (
        <Fragment key={stringifyEntityRef(relation)}>
          {index > 0 && <span>, </span>}
          <Link to={entityPath(relation)}>{labelForRelation(relation)}</Link>
        </Fragment>
      ))}
    </span>
  );
};

const ActionLink = ({
  href,
  icon,
  label,
}: {
  href?: string;
  icon: TegelIconName;
  label: string;
}) => {
  if (!href) {
    return null;
  }

  const isExternal = /^https?:\/\//i.test(href);

  const content = (
    <>
      <TdsIcon name={icon} size="20px" svgTitle={label} />
      {label}
    </>
  );

  return isExternal ? (
    <a
      className="tegel-entity-about-action"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {content}
    </a>
  ) : (
    <Link className="tegel-entity-about-action" to={href}>
      {content}
    </Link>
  );
};

const techDocsHref = (entity: Entity) => {
  const annotations = entity.metadata.annotations ?? {};

  if (
    !annotations[TECHDOCS_ANNOTATION] &&
    !annotations[TECHDOCS_EXTERNAL_ANNOTATION]
  ) {
    return undefined;
  }

  let techDocsEntity: Entity | CompoundEntityRef = entity;

  if (annotations[TECHDOCS_EXTERNAL_ANNOTATION]) {
    try {
      techDocsEntity = parseEntityRef(
        annotations[TECHDOCS_EXTERNAL_ANNOTATION],
        {
          defaultKind: entity.kind,
          defaultNamespace: entity.metadata.namespace ?? DEFAULT_NAMESPACE,
        },
      );
    } catch {
      techDocsEntity = entity;
    }
  }

  const ref =
    'metadata' in techDocsEntity
      ? {
          kind: techDocsEntity.kind,
          namespace: techDocsEntity.metadata.namespace ?? DEFAULT_NAMESPACE,
          name: techDocsEntity.metadata.name,
        }
      : {
          kind: techDocsEntity.kind,
          namespace: techDocsEntity.namespace ?? DEFAULT_NAMESPACE,
          name: techDocsEntity.name,
        };

  return techDocsRoutePath(ref);
};

const gitlabBaseUrl = (instance?: string) => {
  const trimmed = instance?.trim();

  if (!trimmed || trimmed === 'gitlab.com') {
    return 'https://gitlab.scania.com';
  }

  const withoutTrailingSlash = trimmed.replace(/\/+$/, '');

  if (/^https?:\/\//i.test(withoutTrailingSlash)) {
    return withoutTrailingSlash;
  }

  return `https://${withoutTrailingSlash}`;
};

const gitlabProjectHref = (entity: Entity) => {
  const annotations = entity.metadata.annotations ?? {};
  const projectSlug = annotations[gitlabProjectSlugAnnotation]?.trim();

  if (!projectSlug) {
    return undefined;
  }

  return `${gitlabBaseUrl(annotations[gitlabInstanceAnnotation])}/${projectSlug.replace(
    /^\/+/,
    '',
  )}`;
};

const TegelEntityAboutCard = () => {
  const { entity } = useEntity();
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
  const systemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const ownerRelationLabel = useResolvedEntityLabels(
    ownedByRelations,
    useCallback(relation => humanizedRelationLabel(relation, 'group'), []),
  );
  const systemRelationLabel = useResolvedEntityLabels(
    systemRelations,
    useCallback(relation => humanizedRelationLabel(relation, 'system'), []),
  );
  const tags = entity.metadata.tags ?? [];
  const sourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);
  const spec = entitySpec(entity);
  const namespace = entity.metadata.namespace ?? DEFAULT_NAMESPACE;
  const description = entity.metadata.description ?? 'No description provided.';
  const sourceHref =
    sourceLocation?.locationTargetUrl ?? gitlabProjectHref(entity);
  const actions = [
    {
      href: sourceHref,
      icon: 'link' as const,
      label: 'View source',
    },
    {
      href: techDocsHref(entity),
      icon: 'document' as const,
      label: 'View TechDocs',
    },
  ].filter(action => Boolean(action.href));

  return (
    <TegelCard
      title="About"
      cardId={`catalog-about-${stringifyEntityRef(entity)}`}
      bodyClassName="tegel-entity-about-card"
    >
      <section className="tegel-entity-about-summary">
        <span className="tegel-entity-about-summary-icon" aria-hidden="true">
          <TdsIcon name="document" size="24px" svgTitle="Entity" />
        </span>
        <div>
          <p className="tegel-entity-about-eyebrow">
            {titleCaseEntityName(entity.kind)}
            {spec?.type ? ` - ${spec.type}` : ''}
          </p>
          <h3>{entityDisplayName(entity)}</h3>
          <p>{description}</p>
        </div>
      </section>

      {actions.length > 0 && (
        <div className="tegel-entity-about-actions" aria-label="Entity actions">
          {actions.map(action => (
            <ActionLink
              key={action.label}
              href={action.href}
              icon={action.icon}
              label={action.label}
            />
          ))}
        </div>
      )}

      <section className="tegel-entity-about-section">
        <h4>Ownership</h4>
        <dl className="tegel-entity-about-owner-grid">
          <MetaItem label="Owner">
            <RelationLinks
              defaultKind="group"
              labelForRelation={ownerRelationLabel}
              relations={ownedByRelations}
            />
          </MetaItem>
          <MetaItem label="System">
            <RelationLinks
              defaultKind="system"
              labelForRelation={systemRelationLabel}
              relations={systemRelations}
            />
          </MetaItem>
        </dl>
      </section>

      <section className="tegel-entity-about-section">
        <h4>Catalog Details</h4>
        <dl className="tegel-entity-about-fields">
          <MetaItem label="Kind">{titleCaseEntityName(entity.kind)}</MetaItem>
          <MetaItem label="Type">{spec?.type ?? '-'}</MetaItem>
          <MetaItem label="Lifecycle">{spec?.lifecycle ?? '-'}</MetaItem>
          <MetaItem label="Namespace">{namespace}</MetaItem>
        </dl>
      </section>

      <section className="tegel-entity-about-section">
        <h4>Tags</h4>
        {tags.length ? (
          <span className="tegel-entity-about-tags">
            {tags.map(tag => (
              <span key={tag}>{tag}</span>
            ))}
          </span>
        ) : (
          <p className="tegel-entity-about-empty">No tags</p>
        )}
      </section>
    </TegelCard>
  );
};

export const GridEntityAboutCard = ({
  variant = 'gridItem',
}: GridEntityAboutCardProps) => (
  <div className={gridCardClassName(variant)}>
    <TegelEntityAboutCard />
  </div>
);
